import { useState, useMemo, useEffect } from "react"

import { XIcon, PlusIcon, CheckIcon } from "lucide-react"
import { Input, DefaultPopover, Separator, DefaultTooltip, Button, CommandSearch, CommandItemSearch, CommandGroupSearch } from "twyne-new.ui"

import { TailwindSandboxPure } from "@/modules/cms/views/post/components/builder/Content/TailwindSandbox"
import { useResponsive, useUtilities } from "@/modules/cms/views/post/components/builder/context"

import { parseTransform, useCraftProp } from "../../utils"
import { UNIT_OPTIONS } from "../../utils/predefined_sizes"
import ApplyButtonPopover from "../components/ApplyButtonPopover"

import CustomTransform, { TransformStyle, PositionOrigin, Perspective, Backface } from "./Custom"
import DefaultTransform from "./Default"
import RotateDegrees from "./RotateDegrees"

export default function TansformWrapper() {
  const { device } = useResponsive()
  const { selectedUtilities } = useUtilities()

  return <>
    <TransformProperty key={device + selectedUtilities} />
  </>
}

const TransformProperty = () => {
  const [open, setOpen] = useState(false)
  const [transform, setTransform] = useState()

  const [displayCustomProps, setDisplayCustomProps] = useState()

  const { value, setValue } = useCraftProp('transforms')

  useEffect(() => {
    if (displayCustomProps === undefined) {
      const customKeys = TRANSFORM_CONFIG.filter(item => item.custom).map(item => item.value)
      setDisplayCustomProps(customKeys.filter(key => value?.[key] !== undefined))
    }
  }, [displayCustomProps, value])

  useEffect(() => {
    if (!open) {
      return setTransform(undefined)
    }

    if (transform === undefined) {
      setTransform(value || { defaultItems: [{ name: 'rotate', type: 'all', value: '0', category: 'default' }] })
    }
  }, [transform, value, open])

  const handleAddTransform = transformValue => {
    const item = TRANSFORM_CONFIG.find(item => item.value === transformValue)
    const { custom, config = {} } = item

    if (custom) {
      const isDisplayed = displayCustomProps.includes(transformValue)
      return setDisplayCustomProps(isDisplayed ? displayCustomProps.filter(item => item !== transformValue) : [...displayCustomProps, transformValue])
    }

    const { defaultType, defaultValue } = config

    setTransform((prev = {}) => ({
      ...prev,
      defaultItems: [
        ...prev.defaultItems || [],
        { name: transformValue, type: defaultType, value: defaultValue },
      ],
    }))
  }

  const handleApply = () => {
    setValue(transform)
  }

  const handleCancel = () => {
    setTransform()
    setOpen(false)
  }

  const handleChangeDefault = value => {
    setTransform(prev => ({ ...prev, defaultItems: value }))
  }

  return <>
    <InputTrigger defaultItems={value?.defaultItems} onClick={() => setOpen(true)} />
    <DefaultPopover
      wrapperProps={{ open, onOpenChange: setOpen }}
      trigger={<div></div>}
      triggerProps={{ asChild: true }}
      contentProps={{ className: 'w-80', side: 'left', align: 'center' }}
    >
      <div className="w-full">
        <Title
          displayCustomProps={displayCustomProps}
          handleAddTransform={handleAddTransform}
          onClose={() => setOpen(false)}
        />

        <Separator className='m-0 my-2' />

        {transform && <>
          <div className='flex flex-col gap-2 max-h-[500px] overflow-y-auto'>
            <DefaultTransform
              transform={transform?.defaultItems}
              onChange={handleChangeDefault}
            />

            <CustomTransform
              displayCustomProps={displayCustomProps}
              transform={transform}
              onChange={setTransform}
            />
          </div>
        </>}

        <Separator className='m-0 my-2' />
        <Preview transform={transform} />
        <ApplyButtonPopover
          onApply={handleApply}
          onCancel={handleCancel}
          name='transforms'
          value={transform}
        />
      </div>

    </DefaultPopover>
  </>
}

const Title = ({ displayCustomProps, handleAddTransform, onClose }) => <>
  <div className='flex justify-between items-center'>
    <div className='flex gap-2 items-center '>
      Transform
      <InputsConfiguration displayCustomProps={displayCustomProps} onAdd={handleAddTransform} />
    </div>

    <XIcon size='16' className='cursor-pointer' onClick={onClose} />
  </div>
</>

const Preview = ({ transform }) => {
  const defaultClasses = 'w-fit rounded-xs py-2 px-3 text-gray-100 bg-blue-500 cursor-pointer text-xs'
  const transformClasses = parseTransform(transform)

  return <>
    Preview
    <div className='my-2'>
      <TailwindSandboxPure className='w-full h-[100px]'>
        <div className="flex items-center justify-center flex-col gap-2 h-full">
          <div className={`${defaultClasses} ${transformClasses}`} >
            Button
          </div>
        </div>
      </TailwindSandboxPure>
    </div>
    <Separator className='m-0 my-2' />
  </>
}

const InputTrigger = ({ defaultItems, onClick }) => {
  const inputValue = useMemo(() => defaultItems?.map(({ name, type, value }) => `${name}${type === 'all' || !type ? '' : `-${type}`}: ${value}`).join(', '), [defaultItems])

  return <div className='flex items-center gap-1 relative' onClick={onClick}>
    <Input value={inputValue || ''} readOnly variant='accent' onClick={onClick} />
  </div>
}

const InputsConfiguration = ({ displayCustomProps, onAdd }) => <>
  <DefaultPopover trigger={<ButtonTriggerAdvanced />} contentProps={{ side: 'top' }} >
    <CommandSearch placeholder='Type to search a transform...'>
      <Group
        heading='Default'
        items={TRANSFORM_CONFIG.filter(item => !item.custom)}
        onAdd={onAdd}
        displayCustomProps={displayCustomProps}
      />
      <Group
        heading='Custom'
        items={TRANSFORM_CONFIG.filter(item => item.custom)}
        onAdd={onAdd}
        displayCustomProps={displayCustomProps}
      />
    </CommandSearch>
  </DefaultPopover>
</>

const Group = ({ heading, items, onAdd, displayCustomProps }) => <>
  <CommandGroupSearch heading={heading}>
    {items.map(({ value, custom, label }) => <Item
      label={label}
      key={value}
      value={value}
      onAdd={onAdd}
      active={custom && displayCustomProps.includes(value)}
    />)}
  </CommandGroupSearch>
  <Separator />
</>

const Item = ({ value, onAdd, active, label }) => <>
  <div onClick={() => onAdd(value)} >
    <CommandItemSearch key={value?.name || value}>
      <div className="flex items-center">
        {label || value}
        {active && <CheckIcon size='12' className='ml-1' />}
      </div>
    </CommandItemSearch>
  </div>
</>

const ButtonTriggerAdvanced = () => <DefaultTooltip side='right' content='Add transform property' triggerProps={{ asChild: true }}>
  <Button variant='light' size='sm'>
    <PlusIcon size='14' />
  </Button>
</DefaultTooltip>

const UNITS_ANGLE = [
  { label: 'deg', value: 'deg' },
  { label: 'turn', value: 'turn' },
  { label: 'rad', value: 'rad' },
  { label: 'grad', value: 'grad' },
]

export const TRANSFORM_CONFIG = [
  {
    label: 'Rotate',
    value: 'rotate',
    custom: false,
    config: {
      defaultValue: '0',
      defaultType: 'all',
      min: 0,
      max: 360,
      types: ['all', 'x', 'y'],
      units: UNITS_ANGLE,
      defaultUnit: 'deg',
      getAllClass: value => `rotate(${value})`,
      customInput: RotateDegrees,
    },
  },
  {
    label: 'Scale',
    value: 'scale',
    custom: false,
    config: {
      defaultValue: '1',
      defaultType: 'all',
      min: 0,
      max: 10,
      step: 0.1,
      types: ['all', 'x', 'y'],
      getAllClass: value => `scale(${value})`,
    },
  },
  {
    label: 'Translate',
    value: 'translate',
    custom: false,
    config: {
      defaultValue: '0',
      defaultType: 'all',
      defaultUnit: 'px',
      min: -100,
      max: 100,
      types: ['all', 'x', 'y'],
      units: UNIT_OPTIONS,
      getAllClass: value => `translate(${value},${value})`,
    },
  },
  {
    label: 'Skew',
    value: 'skew',
    custom: false,
    config: {
      defaultValue: '0',
      defaultType: 'all',
      defaultUnit: 'deg',
      min: -50,
      max: 50,
      types: ['all', 'x', 'y'],
      units: UNITS_ANGLE,
      getAllClass: value => `skew(${value},${value})`,
    },
  },
  {
    label: 'Arbitrary',
    value: 'arbitrary',
    custom: false,
    config: {
      types: ['all'],
      getAllClass: value => value,
    },
  },
  {
    label: 'Perspective',
    value: 'perspective',
    custom: true,
    config: {
      component: Perspective,
    },
  },
  {
    label: 'Perspective Origin',
    value: 'perspectiveOrigin',
    custom: true,
    config: {
      component: PositionOrigin,
      props: {
        name: 'perspectiveOrigin',
      },
    },
  },
  {
    label: 'Transform Origin',
    value: 'transformOrigin',
    custom: true,
    config: {
      component: PositionOrigin,
      props: {
        name: 'transformOrigin',
      },
    },
  },
  {
    label: 'Backface Visibility',
    value: 'backface',
    custom: true,
    config: {
      component: Backface,
    },
  },
  {
    label: 'Transform Style',
    value: 'transformStyle',
    custom: true,
    config: {
      component: TransformStyle,
    },
  },
]
